import React, { useEffect, useRef, useState } from "react";
import { X, Crop } from "lucide-react";
import { toast } from "sonner";

const ASPECTS = [
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
];

const STAGE_W = 440;

// Drag-to-pan + zoom image cropper with aspect presets; outputs a cropped JPEG File.
export default function ImageCropModal({ file, defaultAspect = 16 / 9, onDone, onCancel }) {
  const [img, setImg] = useState(null);
  const [aspect, setAspect] = useState(defaultAspect);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const stageH = STAGE_W / aspect;
  const baseScale = img ? Math.max(STAGE_W / img.width, stageH / img.height) : 1;
  const scale = baseScale * zoom;
  const dispW = img ? img.width * scale : 0;
  const dispH = img ? img.height * scale : 0;
  const clamp = (o) => ({
    x: Math.min(0, Math.max(STAGE_W - dispW, o.x)),
    y: Math.min(0, Math.max(stageH - dispH, o.y)),
  });

  useEffect(() => {
    setOff((o) => clamp(o));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspect, zoom, img]);

  const onPointerDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    setOff(clamp({ x: drag.current.ox + e.clientX - drag.current.sx, y: drag.current.oy + e.clientY - drag.current.sy }));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const applyCrop = () => {
    if (!img) return toast.error("Photo is still loading — try again in a second.");
    const sx = -off.x / scale;
    const sy = -off.y / scale;
    const sw = STAGE_W / scale;
    const sh = stageH / scale;
    const outW = Math.min(1600, Math.round(sw));
    const outH = Math.round(outW / aspect);
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    canvas.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
    canvas.toBlob(
      (blob) => {
        if (!blob) return toast.error("Crop failed — try again.");
        onDone(new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
      },
      "image/jpeg",
      0.9
    );
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-[#374151]/50 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="image-crop-modal" onClick={onCancel}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-lg w-full p-6 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-3">
          <h3 className="text-base font-extrabold font-heading text-[#374151]">Crop Photo</h3>
          <button onClick={onCancel} data-testid="crop-cancel" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div
          className="relative overflow-hidden rounded-2xl bg-[#111827] mx-auto touch-none select-none cursor-grab active:cursor-grabbing"
          style={{ width: "100%", maxWidth: STAGE_W, height: stageH }}
          data-testid="crop-stage"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          {img && (
            <img
              src={img.src}
              alt="Crop preview"
              draggable={false}
              style={{ position: "absolute", left: off.x, top: off.y, width: dispW, height: dispH, maxWidth: "none" }}
            />
          )}
        </div>
        <p className="text-[10px] font-bold text-[#9CA3AF] mt-2 text-center">Drag to position · zoom to resize the crop area</p>

        <div className="flex items-center gap-2 mt-3">
          {ASPECTS.map((a) => (
            <button
              key={a.label}
              onClick={() => setAspect(a.value)}
              data-testid={`crop-aspect-${a.label.replace(":", "x")}`}
              className={`flex-1 px-3 py-2 rounded-full text-[11px] font-extrabold transition-colors ${aspect === a.value ? "bg-[#374151] text-white" : "bg-white border border-[#FCE6E9] text-[#6B7280] hover:bg-[#FCE6E9]"}`}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-3">
          <span className="text-[10px] font-extrabold uppercase text-[#9CA3AF]">Zoom</span>
          <input type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-[#E5A0BC]" data-testid="crop-zoom" />
        </div>

        <button onClick={applyCrop} data-testid="crop-apply" className="w-full mt-4 px-5 py-3 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-extrabold transition-all flex items-center justify-center gap-2">
          <Crop className="w-4 h-4" /> Crop & Use Photo
        </button>
      </div>
    </div>
  );
}
